import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ variant, detail }) => {
        if (!detail?.html) return;
        const activeVariant = variant || detail?.resource;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);

        queueMicrotask(() => {
          if (!newMediaGallery.slideshow) return;
          const slides = newMediaGallery.slideshow.refs?.slides || Array.from(newMediaGallery.querySelectorAll('slideshow-slide'));
          if (!slides || slides.length <= 1) return;

          let targetIndex = -1;

          if (activeVariant) {
            const options = [activeVariant.option1, activeVariant.option2, activeVariant.option3].filter(Boolean);
            for (let i = 0; i < slides.length; i++) {
              const img = slides[i].querySelector('img');
              const altText = (img?.getAttribute('alt') || slides[i].getAttribute('alt') || '').toLowerCase();
              if (altText && options.some(opt => altText.includes(opt.toLowerCase()) || altText.replace(/\s+/g, '').includes(opt.toLowerCase().replace(/\s+/g, '')))) {
                targetIndex = i;
                break;
              }
            }
            if (targetIndex === -1) {
              for (let i = 0; i < slides.length; i++) {
                const img = slides[i].querySelector('img');
                const srcText = (img?.src || slides[i].querySelector('source')?.srcset || '').toLowerCase();
                if (srcText && options.some(opt => {
                  const cleanOpt = opt.toLowerCase().replace(/\s+/g, '');
                  const numMatch = opt.match(/\d+/);
                  const num = numMatch ? numMatch[0] : null;
                  const filenameMatch = srcText.match(/\/([^/?#]+)[^/]*$/);
                  let filename = filenameMatch ? filenameMatch[1] : srcText;
                  filename = filename.replace(/_\d+x(?=\.[^.]+$)/i, '').replace(/@\d+x(?=\.[^.]+$)/i, '');
                  const baseName = filename.split('.')[0] || filename;
                  return (cleanOpt.length > 1 && filename.includes(cleanOpt)) || (num && (filename.startsWith(num + '.') || filename.startsWith(num + '_') || filename.startsWith(num + '-') || new RegExp('(^|[^0-9])' + num + '([^0-9]|$)').test(baseName)));
                })) {
                  targetIndex = i;
                  break;
                }
              }
            }
          }

          if (targetIndex === -1 && activeVariant?.featured_media?.id) {
            const mediaId = String(activeVariant.featured_media.id);
            const foundIdx = slides.findIndex(slide => slide.getAttribute('slide-id') === mediaId || slide.dataset.mediaId === mediaId);
            if (foundIdx !== -1) targetIndex = foundIdx;
          }

          if (targetIndex >= 0) {
            newMediaGallery.slideshow.select(targetIndex, undefined, { animate: false });
          } else {
            // Server renders the selected variant media first; keep slide 0 when no confident match.
            newMediaGallery.slideshow.select(0, undefined, { animate: false });
          }
        });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
